/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Card,
  CardContent,
  CardHeader,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
} from "@repo/ui";
import { useChangePassword } from "utils/apis/user.api";
import Layout from "components/layout";
import { Eye, EyeOff } from "lucide-react";

export default function ChangePassword() {
  const navigate = useNavigate();
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowPassword2, setIsShowPassword2] = useState(false);
  const [isShowPassword3, setIsShowPassword3] = useState(false);

  const {
    mutate: changePassword,
    isPending: changingPassword,
    isSuccess: changingPasswordSuccess,
  } = useChangePassword();

  const formSchema = z
    .object({
      oldPassword: z.string().min(1, {
        message: "Please Enter Old Password",
      }),
      newPassword: z.string().min(1, {
        message: "Please Enter New Password",
      }),
      verifyNewPassword: z.string().min(1, {
        message: "Please Verify New Password",
      }),
    })
    .superRefine((val, ctx) => {
      if (val.newPassword && val.newPassword === val.oldPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["newPassword"],
          message: "New password must different from old password.",
        });
        return true;
      }

      if (val.verifyNewPassword && val.verifyNewPassword !== val.newPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["newPassword"],
          message: "Confirm password didn't match with the new password.",
        });
        return true;
      }
    });

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (changingPasswordSuccess) {
      navigate("/dashboard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changingPasswordSuccess]);

  function onSubmit(values: z.infer<typeof formSchema>) {
    changePassword(values);
  }

  return (
    <Layout>
      <div className="form-container-2">
        <Breadcrumb className="mt-5 mx-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <Link to="/dashboard">Home</Link>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Change Password</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center justify-center">
          <Card style={{ width: "500px" }}>
            <CardHeader>Change Password</CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="oldPassword"
                    render={({ field }) => (
                      <FormItem>
                        <div className="relative">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="absolute left-2 top-1/2 -translate-y-1/2 transform"
                          >
                            <path
                              d="M18 8H17V6C17 3.24 14.76 1 12 1C9.24 1 7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9C13.71 2.9 15.1 4.29 15.1 6V8Z"
                              fill="#A6AEBB"
                            />
                          </svg>
                          <FormControl>
                            <Input
                              size={222}
                              className="pl-10"
                              placeholder="Old Password"
                              type={isShowPassword ? "text" : "password"}
                              {...field}
                            />
                          </FormControl>
                          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */}
                          <div
                            className="absolute right-2 top-1/2 -translate-y-1/2 transform"
                            onClick={() => {
                              setIsShowPassword(!isShowPassword);
                            }}
                            role="button"
                          >
                            {isShowPassword ? (
                              <Eye color="#A6AEBB" />
                            ) : (
                              <EyeOff color="#A6AEBB" />
                            )}
                          </div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <div className="relative">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="absolute left-2 top-1/2 -translate-y-1/2 transform"
                          >
                            <path
                              d="M18 8H17V6C17 3.24 14.76 1 12 1C9.24 1 7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9C13.71 2.9 15.1 4.29 15.1 6V8Z"
                              fill="#A6AEBB"
                            />
                          </svg>
                          <FormControl>
                            <Input
                              size={222}
                              className="pl-10"
                              placeholder="New Password"
                              type={isShowPassword2 ? "text" : "password"}
                              {...field}
                            />
                          </FormControl>
                          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */}
                          <div
                            className="absolute right-2 top-1/2 -translate-y-1/2 transform"
                            onClick={() => {
                              setIsShowPassword2(!isShowPassword2);
                            }}
                            role="button"
                          >
                            {isShowPassword2 ? (
                              <Eye color="#A6AEBB" />
                            ) : (
                              <EyeOff color="#A6AEBB" />
                            )}
                          </div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="verifyNewPassword"
                    render={({ field }) => (
                      <FormItem>
                        <div className="relative">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="absolute left-2 top-1/2 -translate-y-1/2 transform"
                          >
                            <path
                              d="M18 8H17V6C17 3.24 14.76 1 12 1C9.24 1 7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9C13.71 2.9 15.1 4.29 15.1 6V8Z"
                              fill="#A6AEBB"
                            />
                          </svg>
                          <FormControl>
                            <Input
                              size={222}
                              className="pl-10"
                              placeholder="Verify New Password"
                              type={isShowPassword3 ? "text" : "password"}
                              {...field}
                            />
                          </FormControl>
                          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */}
                          <div
                            className="absolute right-2 top-1/2 -translate-y-1/2 transform"
                            onClick={() => {
                              setIsShowPassword3(!isShowPassword3);
                            }}
                            role="button"
                          >
                            {isShowPassword3 ? (
                              <Eye color="#A6AEBB" />
                            ) : (
                              <EyeOff color="#A6AEBB" />
                            )}
                          </div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end ">
                    <Button
                      variant="secondary"
                      type="submit"
                      isLoading={changingPassword}
                    >
                      Update Password
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
